import React, { useEffect, useState } from 'react'

import TextField from 'presentation/shared/components/TextField'
import { dateMask } from 'presentation/utils/masks'
import SupportText from 'presentation/shared/components/SupportText'
import Checkbox from 'presentation/shared/components/Checkbox'
import TextArea from 'presentation/shared/components/TextArea'
import Modal from 'presentation/shared/components/Modal'
import * as S from './styles'
import SelectField from 'presentation/shared/components/SelectField'
import { Hospital } from 'domain/entities/hospital-model'
import { useStores } from 'presentation/hooks/use-stores'
import { SearchDoctors } from 'domain/usecases/doctor/search-doctors'
import AutoComplete from 'presentation/shared/components/AutoComplete'
import Button from 'presentation/shared/components/Button'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { useServices } from 'presentation/hooks/use-services'
import { Specialty } from 'domain/usecases/specialties/load-specialties'
import { DoctorRole } from 'domain/entities/doctor-user-role-model'

type RecommendationProps = {
  loadDoctors?: SearchDoctors
  setToggleInfo?: any
  toggleInfo?: boolean
  recommendation: {
    recommended_doctor_id?: number
    doctor_recommendation_id?: number
    hospitalUnits?: any
    recommendedBy?: {
      doctorName?: string
      doctorId?: number
      doctorSpecialty?: Specialty | string
    }
    recommendedDate?: string
    specialty?: string
    isEmergency?: boolean
    director?: string
    justification?: string
    units?: string[]
    directorObservation?: string
    reasonEmergency?: string
  }
  units?: string[]
}

type DoctorRecommendationFormValues = {
  doctor_recommendation_id: number
  specialty?: { code: number; name: string }
  doctorSpecialty?: string
  recommendedBy?: string
  recommended_by_id?: string
  recommendedDate: string
  units: string
  emergency?: string
  reasonEmergency?: string
  justification?: string
}

const validationSchema = yup.object().shape({
  recommendedBy: yup.string(),
  specialty: yup.object(),
  units: yup.string().required(),
  emergency: yup.string().required(),
  reasonEmergency: yup.string().when('emergency', {
    is: 'emergency-yes',
    then: yup.string().required()
  }),
  justification: yup.string().when('emergency', {
    is: 'emergency-yes',
    then: yup
      .string()
      .required()
      .min(
        70,
        `A sua justificativa está muito curta, por favor detalhe
    melhor o motivo (mínimo 70 caracteres).`
      )
  })
})

const Recommendation = ({
  setToggleInfo,
  toggleInfo,
  recommendation,
  units
}: RecommendationProps) => {
  const [, setSearchSpecialty] = useState('')
  const [hospitals, setHospitals] = useState([] as Hospital[])
  const [, setIsLoading] = useState(true)
  const currentHospital = useStores().currentHospital
  const [handleSuccessModal, setHandleSuccessModal] = useState<boolean>(false)

  const [doctors, setDoctors] = useState<any>([])
  const [doctorQuery, setDoctorQuery] = useState<string>()

  const formik = useFormik({
    initialValues: {} as DoctorRecommendationFormValues,
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        handleSaveRecommendation(values)
        setHandleSuccessModal(true)
        setToggleInfo(!toggleInfo)
      } catch (error) {
        toast.error('Ocorreu um erro ao atualizar indicação')
      }
    }
  })

  const hasIndication =
    recommendation.recommendedBy?.doctorName === 'Doctor - REDFOX'
      ? false
      : true

  useEffect(() => {
    const loadData = async () => {
      await currentHospital.load()
      setHospitals(currentHospital.hospitalList)
    }

    loadData()

    if (recommendation.recommendedBy) {
      formik.setFieldValue(
        'recommendedBy',
        recommendation.recommendedBy.doctorName
      )
    }

    if (recommendation.specialty) {
      setSearchSpecialty(recommendation.specialty)
    }

    if (recommendation.doctor_recommendation_id) {
      formik.setFieldValue(
        'doctor_recommendation_id',
        recommendation.doctor_recommendation_id
      )
    }

    if (recommendation.recommendedBy) {
      formik.setFieldValue(
        'recommended_by_id',
        recommendation.recommendedBy.doctorId
      )
    }

    if (recommendation.justification) {
      formik.setFieldValue('justification', recommendation.justification)
    }

    if (!hasIndication) {
      formik.setFieldValue('recommendedBy', undefined)
    }

    if (recommendation.hospitalUnits) {
      formik.setFieldValue('units', recommendation.hospitalUnits)
    }

    if (recommendation.isEmergency) {
      formik.setFieldValue('emergency', 'emergency-yes')
    }

    if (recommendation.reasonEmergency) {
      formik.setFieldValue('reasonEmergency', recommendation.reasonEmergency)
    } else {
      formik.setFieldValue('emergency', 'emergency-no')
    }

    if (recommendation.recommendedBy?.doctorSpecialty) {
      formik.setFieldValue(
        'doctorSpecialty',
        recommendation.recommendedBy?.doctorSpecialty
      )
    }

    if (recommendation.recommendedBy?.doctorName === 'Doctor - REDFOX') {
      formik.setFieldValue('doctorSpecialty', '')
    }

    setIsLoading(false)
  }, [units])

  const recommendationServices = useServices().recommendation
  const handleSaveRecommendation = async (
    values: DoctorRecommendationFormValues
  ) => {
    return await recommendationServices.updateRecommendation({
      doctor_recommendation_id: values.doctor_recommendation_id,
      updateRecommendationWithoutDoctor: true,
      units: values.units,
      recommended_by_id: Number(values.recommended_by_id),
      specialty: values?.specialty,
      emergency: values.emergency === 'emergency-yes',
      emergencyJustification:
        values.emergency === 'emergency-yes' ? values.justification : '',
      reasonEmergency:
        values.emergency === 'emergency-yes' ? values.reasonEmergency : ''
    })
  }

  const doctorService = useServices().doctor
  const loadDoctors = async (doctorName = '') => {
    const response = await doctorService.searchDoctors({
      doctorName,
      params: ['data{', 'specialty', 'name', 'doctor_id, user_id}, '],
      pagination: { pageNumber: 1, pageSize: 10 }
    })
    setDoctors(response.data)
  }

  async function selectDoctor(doctor: DoctorRole) {
    if (doctor) {
      const placeholder = doctor.name
      setDoctorQuery(placeholder)
      formik.setFieldValue('recommended_by_id', doctor.user_id)
      formik.setFieldValue('doctorSpecialty', doctor.specialty)
    } else {
      await formik.setFieldValue('recommended_by_id', undefined)
      setDoctorQuery(undefined)
    }
  }

  return (
    <S.Wrapper onSubmit={formik.handleSubmit} data-testid="card-recommendation">
      <S.Header>
        <S.InputButtonContainer>
          <AutoComplete
            label="Indicado por"
            suggestions={doctors.map((doctor: DoctorRole) => ({
              label: `${doctor.name} - ${doctor.user_id}`,
              value: doctor
            }))}
            name="recommended_by_id"
            placeholder="Busca por nome ou CRM"
            onSuggestionClick={selectDoctor}
            value={formik.values.recommendedBy || doctorQuery || ''}
            disabled={hasIndication}
            onType={(val) => loadDoctors(val)}
            debounceDelay={1000}
            onInputChange={setDoctorQuery}
            iconPosition="right"
            iconLocale="inside"
            iconMargin="54px"
            required
            data-testid="select-doctor-autocomplete"
          />
        </S.InputButtonContainer>

        <TextField
          onChange={formik.handleChange}
          onInputChange={(value) => {
            formik.setFieldValue('recommendedDate', value)
          }}
          name="recommendedDate"
          label="Data da indicação"
          required
          value={hasIndication ? recommendation.recommendedDate : ''}
          disabled
          mask={dateMask}
          style={{ minWidth: '180px', flex: 0 }}
          data-testid="input-recommended-date"
        />
      </S.Header>

      <S.InputsContainer>
        <TextField
          label="Especialidade"
          required
          value={formik.values.doctorSpecialty}
          disabled
          style={{ minWidth: '180px', flex: 0 }}
        />

        {!hasIndication ? (
          <SelectField
            name="units"
            onChange={formik.handleChange}
            label="Unidade da indicação"
            disabled={hasIndication}
            items={hospitals?.map(({ name, friendly_name, hospital_id }) => ({
              label: friendly_name || name,
              value: hospital_id
            }))}
            value={formik.values.units ? formik.values.units : ''}
            required
          />
        ) : (
          <S.Units style={{ display: 'block' }} data-testid="card-units">
            <SupportText color="primary">Unidades da indicação</SupportText>
            <section>
              {units?.map((unity, key) => (
                <Checkbox key={key} label={unity} value={unity} checked />
              ))}
            </section>
          </S.Units>
        )}
      </S.InputsContainer>

      <S.Complement data-testid="card-complement">
        <S.InputsContainer>
          <SelectField
            disabled={hasIndication}
            name="emergency"
            onChange={formik.handleChange}
            label="Cadastro Emergencial?"
            items={emergencyOptions}
            onInputChange={(emergency) =>
              formik.setFieldValue('emergency', emergency)
            }
            value={formik.values.emergency ?? ''}
            error={
              formik.touched.emergency ? formik.errors.emergency : undefined
            }
            required
          />

          {formik.values.emergency === 'emergency-yes' && (
            <SelectField
              disabled={hasIndication}
              items={defaultEmergencyJustifications}
              label="Motivo do cadastro emergencial?"
              value={formik.values.reasonEmergency ?? ''}
              name="reasonEmergency"
              onInputChange={(reasonEmergency) =>
                formik.setFieldValue('reasonEmergency', reasonEmergency)
              }
              style={{ marginTop: '12px' }}
              required
              error={
                formik.touched.reasonEmergency
                  ? formik.errors.reasonEmergency
                  : undefined
              }
            />
          )}
        </S.InputsContainer>

        {!!formik.values.emergency &&
          formik.values.emergency === 'emergency-yes' && (
            <>
              <S.Justification>
                <TextArea
                  name="justification"
                  onChange={formik.handleChange}
                  label="Justificativa do cadastro emergencial"
                  value={formik.values.justification}
                  disabled={hasIndication}
                  data-testid="input-justification"
                  required
                  error={formik.errors.justification}
                />
              </S.Justification>
            </>
          )}
      </S.Complement>

      {!hasIndication && (
        <S.ActionsButtons>
          <Button type="submit" disabled={hasIndication || !formik.isValid}>
            Salvar
          </Button>
        </S.ActionsButtons>
      )}
      <Modal
        show={handleSuccessModal}
        close={() => setHandleSuccessModal(false)}
        title="Dados atualizados com sucesso"
      />
    </S.Wrapper>
  )
}

export default Recommendation

const emergencyOptions = [
  {
    label: 'Sim',
    value: 'emergency-yes'
  },
  {
    label: 'Não',
    value: 'emergency-no'
  }
]

const defaultEmergencyJustifications = [
  {
    value:
      'Desfalque na escala de plantão, sem tempo hábil para realizar trocas entre a equipe ou realização do cadastro no tempo adequado.',
    label:
      'Desfalque na escala de plantão, sem tempo hábil para realizar trocas entre a equipe ou realização do cadastro no tempo adequado.'
  },
  {
    value: 'Cirurgia agendada para médico com pendência de cadastro.',
    label: 'Cirurgia agendada para médico com pendência de cadastro.'
  },
  {
    value: 'Cadastro para Profissional Convidado.',
    label: 'Cadastro para Profissional Convidado.'
  },
  {
    value: 'Pedido da Diretoria.',
    label: 'Pedido da Diretoria.'
  }
]
